import { useMemo } from 'react';
import { Col } from 'reactstrap';
import {
  Select,
  SelectOptionType,
  TextAreaInput,
  TextInput,
} from '@octano/global-ui';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { TranslationsKeys } from '../../../../locales/translations';
import { useValidations } from '../../../../hooks/useValidations';
import MultipleFileInput from '../../../../components/fileInput/MultipleFileInput';
import PetitionFileDownload from './PetitionFileDownload';
import { PetitionData, ViewPetition } from '../../../../types/petitions';
import {
  deleteTempFile,
  uploadTempFile,
} from '../../../../api/requests/petitions';

interface PetitionFormFreezeCreditsProps {
  careerOptions?: SelectOptionType[];
  maxCreditsToFreeze?: number;
  downloadFiles?: ViewPetition['docs'];
  downloadFileResponse?: ViewPetition['docResponse'];
  onlyView?: boolean;
  isResolved?: boolean;
  onLoadingChange?: (loading: boolean) => void;
  periodName?: string;
}

export default function PetitionFormFreezeCredits({
  careerOptions = [],
  maxCreditsToFreeze,
  onlyView = false,
  isResolved = false,
  downloadFiles,
  downloadFileResponse,
  onLoadingChange,
  periodName,
}: PetitionFormFreezeCreditsProps) {
  const { t } = useTranslation(TranslationsKeys.PETITIONS);
  const {
    validateTextNotEmpty,
    validatePhone,
    validateEmail,
  } = useValidations();
  const { control, setValue } = useFormContext<PetitionData>();

  const text = useMemo(() => {
    const prefix = 'modal.form';
    return {
      studyPlan: t(`${prefix}.studyPlan`),
      phone: t(`${prefix}.phone`),
      email: t(`${prefix}.email`),
      reason: {
        label: t(`${prefix}.reason.label`),
        placeholder: t(`${prefix}.reason.placeholder`),
      },
      file: {
        label: t(`${prefix}.file.label`),
        button: t(`${prefix}.file.button`),
      },
      download: {
        label: t(`${prefix}.download.label`),
        labelResponse: t(`${prefix}.download.labelResponse`),
        button: t(`${prefix}.download.button`),
      },
      validation: { select: t(`${prefix}.validation.select`) },
      freezeSemesterOrCredits: {
        contractedCredits: t(
          `${prefix}.freezeSemesterOrCredits.contractedCredits`,
        ),
        enrolledCredits: {
          label: t(`${prefix}.freezeSemesterOrCredits.enrolledCredits.label`),
        },
        availableCreditsToFreeze: t(
          `${prefix}.freezeSemesterOrCredits.availableCreditsToFreeze`,
        ),
        creditsToFreeze: {
          label: t(`${prefix}.freezeSemesterOrCredits.creditsToFreeze.label`),
          errors: {
            required: t(
              `${prefix}.freezeSemesterOrCredits.creditsToFreeze.errors.required`,
            ),
            min: t(
              `${prefix}.freezeSemesterOrCredits.creditsToFreeze.errors.min`,
            ),
            max: t(
              `${prefix}.freezeSemesterOrCredits.creditsToFreeze.errors.max`,
            ),
            notValid: t(
              `${prefix}.freezeSemesterOrCredits.creditsToFreeze.errors.notValid`,
            ),
          },
        },
      },
    };
  }, [t]);

  setValue('title', `Congelación de créditos ${periodName}`.trim());

  return (
    <>
      <Col md={8} className="my-3">
        <Select
          label={text.studyPlan}
          control={control}
          disabled={onlyView}
          options={careerOptions}
          rules={{ required: text.validation.select }}
          name="studyPlanEnrollment"
          isClearable={false}
        />
      </Col>
      <Col md={4} className="my-3">
        <TextInput
          label="Período"
          name="periodName"
          value={periodName}
          disabled
        />
      </Col>
      <Col md={6} className="my-3">
        <TextInput
          label={text.phone}
          name="cellPhone"
          disabled={onlyView}
          control={control}
          rules={{ validate: { validatePhone, require: validateTextNotEmpty } }}
        />
      </Col>
      <Col md={6} className="my-3">
        <TextInput
          label={text.email}
          name="email"
          control={control}
          disabled={onlyView}
          rules={{ validate: { validateEmail, require: validateTextNotEmpty } }}
        />
      </Col>

      <Col md={6} className="my-3">
        <TextInput
          label={text.freezeSemesterOrCredits.contractedCredits}
          name="contractedCredits"
          control={control}
          formatter={(value) => String(value ?? '')}
          disabled
        />
      </Col>

      <Col md={6} className="my-3">
        <TextInput
          label={text.freezeSemesterOrCredits.enrolledCredits.label}
          name="enrolledCredits"
          control={control}
          formatter={(value) => String(value ?? '')}
          disabled
        />
      </Col>

      <Col md={6} className="my-3">
        <TextInput
          label={text.freezeSemesterOrCredits.availableCreditsToFreeze}
          name="maxCreditsToFreeze"
          control={control}
          formatter={(value) => String(value ?? '')}
          disabled
        />
      </Col>
      <Col md={6} className="my-3">
        <TextInput
          label={text.freezeSemesterOrCredits.creditsToFreeze.label}
          name="creditsToFreeze"
          control={control}
          formatter={(value) => String(value ?? '')}
          disabled={!maxCreditsToFreeze}
          rules={{
            required:
              text.freezeSemesterOrCredits.creditsToFreeze.errors.required,
            min: {
              value: 1,
              message: text.freezeSemesterOrCredits.creditsToFreeze.errors.min,
            },
            max: {
              value: maxCreditsToFreeze!,
              message: text.freezeSemesterOrCredits.creditsToFreeze.errors.max,
            },
            validate: {
              isNumber: (value) => {
                const numValue = Number(value);
                return (
                  !isNaN(numValue) ||
                  text.freezeSemesterOrCredits.creditsToFreeze.errors.required
                );
              },
            },
          }}
        />
      </Col>
      <Col md={12}>
        <TextAreaInput
          label={text.reason.label}
          name="description"
          placeholder={text.reason.placeholder}
          disabled={onlyView}
          rules={{
            validate: { require: validateTextNotEmpty },
            maxLength: { value: 500, message: 'Max 500' },
          }}
          control={control}
        />
      </Col>
      <Col md={12} className="my-4">
        {onlyView ? (
          <PetitionFileDownload
            text={text.download.button}
            label={text.download.label}
            files={downloadFiles}
          />
        ) : (
          <MultipleFileInput
            name="docs"
            label={text.file.label}
            accept="image/png, image/jpeg, application/pdf"
            control={control}
            uploadTempFile={uploadTempFile}
            deleteTempFile={deleteTempFile}
            onLoadingChange={onLoadingChange}
          />
        )}
      </Col>
      {isResolved && downloadFileResponse && (
        <Col md={12} className="my-4">
          <PetitionFileDownload
            text={text.download.button}
            label={text.download.labelResponse}
            file={downloadFileResponse}
          />
        </Col>
      )}
    </>
  );
}
